import { Box, Typography, LinearProgress } from "@mui/material";
import {
	BudgetConsumption,
	BudgetLimitEnforcementMode,
} from "@/domain/program.interface";

interface BudgetConsumptionBarProps {
	consumption: BudgetConsumption;
	threshold?: number;
	mode?: BudgetLimitEnforcementMode;
}

const money = (n: number): string => `$${Math.round(n).toLocaleString()}`;

/**
 * SIMS-6076 — read-only budget consumption indicator (Program Maintenance).
 * Shows net earned vs budget limit, e.g. "$4,200 of $10,000 consumed — 42%".
 */
export default function BudgetConsumptionBar({
	consumption,
	threshold,
	mode,
}: BudgetConsumptionBarProps): JSX.Element {
	const { netEarned, budgetLimit, percentage } = consumption;
	const over = threshold != null && percentage >= threshold;
	const near = threshold != null && !over && percentage >= threshold - 10;
	const color: "success" | "warning" | "error" =
		over && mode === "BLOCK" ? "error" : over || near ? "warning" : "success";

	return (
		<Box
			data-testid="program-budget-consumption"
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1,
				p: 2,
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "baseline",
					mb: 1,
				}}
			>
				<Typography variant="body2" fontWeight={600}>
					Budget consumption
				</Typography>
				<Typography
					variant="body2"
					fontWeight={700}
					data-testid="program-budget-consumption-value"
				>
					{money(netEarned)} of {money(budgetLimit)} consumed — {percentage}%
				</Typography>
			</Box>
			<LinearProgress
				variant="determinate"
				value={Math.min(percentage, 100)}
				color={color}
				sx={{ height: 10, borderRadius: 5 }}
			/>
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", mt: 1 }}
			>
				Net earned incentives across all enrollments (active, completed, and
				disenrolled): EARNED + EARNED_BANKED − REVERT.
			</Typography>
		</Box>
	);
}
